/* cardLedgers.js — the canonical consolidated ledger for every GOLD DRUG CARD
   tooltip (the 32 non-keystone gold equips in backend/services/golden_cards_data.json).
   Keystones carry their own beatLedger/healLedger in keystoneCards.jsx.

   Each card collapses to ONE green chip (its primary benefit) + ONE red chip (its
   primary cost, or null for a pure-upside card) — the canonical CanonicalEffectChip:
   glyph + resource word by default, hover reveals ONLY the number + magnitude.

   ACCURACY (tooltip-canon.md §D): every `detail` is the ENGINE'S ACTUAL behavior,
   not the authored pros/cons prose. Derived + adversarially parity-checked against
   the live engine by the propagation workflow (wf_c5e013a6, 2026-06-26): each card's
   effect.params consumer was grepped out (most of these are NOT batch_combat_engine
   effects — they modify XP / currency / session / progression / PvP), and 3 refute
   skeptics verified sign / magnitude / unit / per-stack against it.

   The 2026-06-26 engine parity pass RESOLVED the three earlier findings (branch
   fix/engine-tooltip-parity-gaps), so the chips below match the now-fixed engine:
     - Phenytoin crash self-damage: was legacy-only; now also lands in batch
       (deck-boss) combat (batch_combat_engine.py:4405). The red chip drops its
       old "in boss and exam fights" qualifier -- the crash fires everywhere now.
     - Methylene Blue exclude_sources: removed as vestigial (health_service.py
       :386-396) -> the overworld block is unconditional by design; the green
       "blocks all overworld HP loss" chip stands, still no red chip.
     - Warfarin lifesteal: heals 15% of resolved batch damage. Each production
       event adds 1 INR; above the therapeutic ceiling of 3, the track bleeds
       3 HP each round.

   No em dashes (project rule). Keyed by normalized generic_name. */

const LEDGERS = {
  // double_edge (batch_combat_engine.py: x2 on correct hits, boss attack doubled each round)
  digoxin: {
    green: { word: 'Damage', detail: 'x2 dealt' },
    red: { word: 'Damage taken', detail: 'x2 taken' },
  },
  // streak_shield (batch_combat_engine.py:5503-5733: one wrong answer per fight keeps the streak)
  propranolol: {
    green: { word: 'Streak', detail: 'held once per fight on a wrong answer' },
    red: null,
  },
  // revive (one death per fight restored to full HP)
  naloxone: {
    green: { word: 'Revive', detail: 'to full HP, once per fight' },
    red: null,
  },
  // mild_analgesic (flat_reduction: -3 boss damage per living enemy each round)
  acetaminophen: {
    green: { word: 'Block', detail: '-3 boss damage per enemy each round' },
    red: null,
  },
  // anxiolytic_tolerance (self_damage_reduction 0.5 + streak_threshold lowered 0.75 -> 0.4)
  hydroxyzine: {
    green: { word: 'Steady', detail: '-50% self-damage, streak holds at 40% partial score' },
    red: null,
  },
  // altered_perception (heal_per_correct +1 HP, batch_size_bonus +1; degrades active cards on use)
  dronabinol: {
    green: { word: 'Heal', detail: '+1 HP per correct, +1 question per batch' },
    red: { word: 'Degrade', detail: '15% of active cards on use (min 3, max 20)' },
  },
  // stimulant_session (xp_mult + currency_mult 2.5x for 3h; batch_size_reduction -1)
  amphetamine: {
    green: { word: 'Boost', detail: 'x2.5 XP and currency for 3h' },
    red: { word: 'Short batch', detail: '-1 question per batch' },
  },
  // neural_rewire (tolerance reset + immunities, promote 5 cards, +500 XP/currency; 7-day cooldown)
  psilocybin: {
    green: { word: 'Rewire', detail: 'clears tolerance, +72h tolerance immunity, +24h comedown immunity, promotes 5 cards, +500 XP and currency' },
    red: { word: 'Cooldown', detail: '7-day lockout after use' },
  },
  // lifesteal + keystone-agnostic INR liability track (batch_combat_engine.py).
  warfarin: {
    green: { word: 'Heal', detail: '15% of resolved batch damage' },
    red: { word: 'Bleed', detail: '-3 HP each round above 3 INR; +1 INR per lifesteal' },
  },
  // second_wind (bonus_mult x2 on the next correct hit after a wrong answer)
  albuterol: {
    green: { word: 'Damage', detail: 'x2 on your next correct hit after a miss' },
    red: null,
  },
  // glycogen_reserve (spend up to 25 banked glycogen to survive a lethal hit; cap 50)
  glucagon: {
    green: { word: 'Survive', detail: 'spend up to 25 glycogen to survive a lethal hit (bank cap 50)' },
    red: null,
  },
  // basal_shield (-30% dmg taken below 50% HP, -40% below 25% HP)
  insulin_glargine: {
    green: { word: 'Block', detail: '-30% damage below 50% HP, -40% below 25%' },
    red: null,
  },
  // bolus_guard (-30% dmg taken at 75%+ HP, -40% at 85%+ HP)
  insulin_lispro: {
    green: { word: 'Block', detail: '-30% damage at 75%+ HP, -40% at 85%+' },
    red: null,
  },
  // open_formulary (bypass all level + objective locks)
  mannitol: {
    green: { word: 'Unlock', detail: 'all levels and objectives open' },
    red: null,
  },
  // immunosuppressant (-35% wrong-answer self-damage; -90% healing, -10% with Folic Acid)
  methotrexate: {
    green: { word: 'Damage', detail: '-35% wrong-answer self-damage' },
    red: { word: 'Healing', detail: '-90% received, -10% with Folic Acid' },
  },
  // cytotoxic_fury (+50% boss damage on every correct; -5 HP self-damage per correct)
  cyclophosphamide: {
    green: { word: 'Damage', detail: '+50% on every correct' },
    red: { word: 'Self-damage', detail: '-5 HP per correct' },
  },
  // zero_order (+3 damage per stack, pre-multiplier, uncapped; wrong answer crashes stacks)
  phenytoin: {
    green: { word: 'Stacks', detail: '+3 damage per stack, uncapped' },
    red: { word: 'Crash', detail: 'a wrong answer resets stacks; 2x stacks self-damage' },
  },
  // folate_rescue (healing lands at 90% under Methotrexate, vs 10% alone)
  folic_acid: {
    green: { word: 'Healing', detail: 'lands at 90% under Methotrexate (vs 10%)' },
    red: null,
  },
  // cocky (pvp win reward: +$75 and +75 XP)
  testosterone: {
    green: { word: 'PvP', detail: '+$75 and +75 XP on a win' },
    red: null,
  },
  // rapid_absorption (xp_mult 1.25 from teaching, trials, drills)
  caffeine: {
    green: { word: 'XP', detail: '+25% from teaching, trials, and drills' },
    red: null,
  },
  // insulin_sensitizer (bonus_tokens +1 per drug game)
  metformin: {
    green: { word: 'Tokens', detail: '+1 per drug game' },
    red: null,
  },
  // hyperfocus (streak_table x1.2/1.4/1.6/1.8 at 3/5/8/10; wrong answer resets)
  methylphenidate: {
    green: { word: 'Streak', detail: 'x1.2 / 1.4 / 1.6 / 1.8 at 3 / 5 / 8 / 10' },
    red: { word: 'Crash', detail: 'a wrong answer resets the streak' },
  },
  // cholinesterase_inhibitor (+1 flat weight per correct drug-game answer)
  donepezil: {
    green: { word: 'Learning', detail: '+1 weight per correct drug-game answer' },
    red: null,
  },
  // nmda_antagonist (1 wrong per session recounted as correct for XP)
  memantine: {
    green: { word: 'Forgive', detail: '1 wrong per session counts as correct for XP' },
    red: null,
  },
  // dopamine_precursor (token_mult x1.3 from fountain nodes)
  levodopa: {
    green: { word: 'Tokens', detail: 'x1.3 from fountain nodes' },
    red: null,
  },
  // auxiliary_pathway (first_visit_xp +10, once per subtopic first visit)
  gabapentin: {
    green: { word: 'XP', detail: '+10 once per new subtopic' },
    red: null,
  },
  // indigo_mantle -- blocks ALL overworld persistent HP loss unconditionally
  // (exclude_sources removed 2026-06-26 as vestigial, health_service.py:386-396).
  methylene_blue: {
    green: { word: 'Shield', detail: 'blocks all overworld HP loss' },
    red: null,
  },
  // vancomycin_keystone — the gold card equips the Vancomycin keystone. Kept in
  // PARITY with keystoneCards.jsx (the canvas keystone card's hover panel reads this
  // ledger via getCardLedger): ONE broad "Precise Dosing" chip, no number on hover and
  // NO "Miss" chip, because the Vancomycin beat MECHANIC is still being authored
  // (Edward 2026-06-27). Do NOT re-add the x2-crit / miss specifics until it is final.
  vancomycin: {
    green: { word: 'Precise Dosing', detail: null },
    red: null,
  },
  // adalimumab_keystone — the gold card equips the Adalimumab keystone (SATA dodge tokens).
  // A miss deals 0 boss damage AND 0 self-damage (dupilumab.py:113-117), then resets the streak.
  adalimumab: {
    green: { word: 'Dodge', detail: '+1 token per correct SATA, blocks a hit' },
    red: { word: 'Miss', detail: '0 boss damage, 0 self-damage, streak reset' },
  },
  // ramp_suppression (boss damage hard-capped at its round-1 value every later round)
  ibuprofen: {
    green: { word: 'Block', detail: 'boss damage capped at its round-1 value' },
    red: null,
  },
  // buffered_recovery (+5 HP round 1, then 4/3/2/1, floor +1 HP/round)
  sodium_bicarbonate: {
    green: { word: 'Heal', detail: '+5 HP, then 4 / 3 / 2 / 1 each round, floor +1' },
    red: null,
  },
  // ketone_charges (build 1/round, +30 HP burst at 5 charges)
  empagliflozin: {
    green: { word: 'Heal', detail: '+30 HP burst at 5 ketone charges' },
    red: null,
  },
};

/* Normalize a generic_name to the LEDGERS key shape: lowercase, separators -> _. */
function normalizeKey(name) {
  return String(name || '').toLowerCase().trim().replace(/[\s-]+/g, '_');
}

/* Return the consolidated ledger chip array for a drug card's generic_name, in
   render order (green then red), each as a {tone, word, detail} CanonicalEffectChip
   prop bag. Drugs use tone 'pos' (green) / 'neg' (red); the keystone 'good' accent
   tone is reserved for keystone cards (keystoneCards.jsx). Returns null when the
   card has no authored ledger so PillCard falls back to the legacy pros/cons htags. */
export function getCardLedger(genericName) {
  const entry = LEDGERS[normalizeKey(genericName)];
  if (!entry) return null;
  const chips = [];
  if (entry.green) chips.push({ tone: 'pos', word: entry.green.word, detail: entry.green.detail });
  if (entry.red) chips.push({ tone: 'neg', word: entry.red.word, detail: entry.red.detail });
  return chips.length ? chips : null;
}

export default LEDGERS;
